import React from 'react';
import { 
  AlertTriangle, Shield, AlertCircle, TrendingUp, TrendingDown, 
  Clock, Phone, Activity, CheckCircle2, XCircle, BarChart3 
} from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PhoneActivityTimeline } from '@/lib/api';

interface PhoneRiskIndicatorProps {
  phoneData: PhoneActivityTimeline;
  phoneNumber: string;
  showTimeline?: boolean;
}

export function PhoneRiskIndicator({ phoneData, phoneNumber, showTimeline = true }: PhoneRiskIndicatorProps) {
  const getRiskConfig = (status: string) => {
    switch (status) {
      case 'high-risk':
        return {
          icon: AlertTriangle,
          label: 'High Risk',
          color: 'red',
          bgGradient: 'from-red-500 to-rose-600',
          lightBg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          progressBg: 'bg-red-500',
          badgeBg: 'bg-red-100 text-red-700 border-red-200'
        };
      case 'moderate-risk':
        return {
          icon: AlertCircle,
          label: 'Moderate Risk',
          color: 'amber',
          bgGradient: 'from-amber-500 to-orange-600',
          lightBg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-700',
          progressBg: 'bg-amber-500',
          badgeBg: 'bg-amber-100 text-amber-700 border-amber-200'
        };
      default:
        return {
          icon: Shield,
          label: 'Low Risk',
          color: 'green',
          bgGradient: 'from-green-500 to-emerald-600',
          lightBg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-700',
          progressBg: 'bg-green-500',
          badgeBg: 'bg-green-100 text-green-700 border-green-200'
        };
    }
  };

  const config = getRiskConfig(phoneData.status);
  const RiskIcon = config.icon;

  const confirmationRate = phoneData.total_reports > 0 
    ? (phoneData.confirmed_reports / phoneData.total_reports) * 100 
    : 0;

  const activityTrend = phoneData.confirmed_reports > phoneData.disputed_reports ? 'increasing' : 'stable';

  return (
    <Card className="overflow-hidden border border-slate-200/80 shadow-xl bg-white/90 backdrop-blur-sm">
      {/* Header with gradient */}
      <div className={`bg-gradient-to-r ${config.bgGradient} p-6 text-white`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <RiskIcon className="h-7 w-7" />
            </div>
            <div>
              <p className="text-white/80 text-sm font-medium">Risk Assessment</p>
              <h3 className="text-2xl font-bold font-mono tracking-wide">{phoneNumber}</h3>
            </div>
          </div>
          <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 text-sm px-4 py-1.5">
            {config.label}
          </Badge>
        </div>
      </div>
      
      <CardContent className="p-6 space-y-6">
        {/* Risk Score */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Risk Score</span>
            </div>
            <span className="text-2xl font-bold text-slate-900">{phoneData.risk_score}<span className="text-sm text-slate-400 font-normal">/100</span></span>
          </div>
          <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`absolute inset-y-0 left-0 ${config.progressBg} rounded-full transition-all duration-500`}
              style={{ width: `${phoneData.risk_score}%` }}
            />
            {/* Markers */}
            <div className="absolute inset-0 flex">
              <div className="w-1/3 border-r border-white/50" />
              <div className="w-1/3 border-r border-white/50" />
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Based on {phoneData.total_reports} reports and community verification
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative overflow-hidden bg-slate-50 rounded-xl p-4 text-center border border-slate-100">
            <div className="text-3xl font-bold text-slate-800">{phoneData.total_reports}</div>
            <div className="text-xs text-slate-500 mt-1">Total Reports</div>
          </div>
          <div className="relative overflow-hidden bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 text-center border border-red-100">
            <CheckCircle2 className="absolute -right-2 -top-2 h-12 w-12 text-red-100" />
            <div className="relative text-3xl font-bold text-red-600">{phoneData.confirmed_reports}</div>
            <div className="relative text-xs text-red-600/70 mt-1">Confirmed</div>
          </div>
          <div className="relative overflow-hidden bg-slate-50 rounded-xl p-4 text-center border border-slate-100">
            <XCircle className="absolute -right-2 -top-2 h-12 w-12 text-slate-100" />
            <div className="relative text-3xl font-bold text-slate-500">{phoneData.disputed_reports}</div>
            <div className="relative text-xs text-slate-500 mt-1">Disputed</div>
          </div>
          <div className="relative overflow-hidden bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 text-center border border-orange-100">
            <Activity className="absolute -right-2 -top-2 h-12 w-12 text-orange-100" />
            <div className="relative text-3xl font-bold text-orange-600">{confirmationRate.toFixed(1)}%</div>
            <div className="relative text-xs text-orange-600/70 mt-1">Confirmation Rate</div>
          </div>
        </div>

        {/* Timeline */}
        {showTimeline && phoneData.first_reported && (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">First Reported</p>
                <p className="font-semibold text-slate-800">
                  {format(new Date(phoneData.first_reported), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                <Activity className="h-5 w-5 text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Last Activity</p>
                <p className="font-semibold text-slate-800">
                  {format(new Date(phoneData.last_reported), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Activity Trend */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              activityTrend === 'increasing' ? 'bg-red-100' : 'bg-green-100'
            }`}>
              {activityTrend === 'increasing' ? (
                <TrendingUp className="h-5 w-5 text-red-600" />
              ) : (
                <TrendingDown className="h-5 w-5 text-green-600" />
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500">Fraud Activity</p>
              <p className={`font-semibold ${activityTrend === 'increasing' ? 'text-red-600' : 'text-green-600'}`}>
                {activityTrend === 'increasing' ? 'Increasing' : 'Stable'}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="bg-white border-slate-300 text-slate-600">
            {phoneData.total_reports} reports in timeline
          </Badge>
        </div>

        {/* Risk Warning */}
        {phoneData.status === 'high-risk' && (
          <div className="relative overflow-hidden bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-2xl p-5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
            <div className="relative flex gap-4">
              <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-red-500/30">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-red-800 text-lg mb-2">⚠️ HIGH RISK WARNING</h4>
                <p className="text-red-700 text-sm mb-3">
                  This number has been reported multiple times for fraudulent activities with 
                  a high confirmation rate ({confirmationRate.toFixed(1)}%).
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    'Do not share personal information',
                    'Do not send money or make payments',
                    'Verify caller identity officially',
                    'Report suspicious activity'
                  ].map((tip, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-red-600">
                      <XCircle className="h-3 w-3 flex-shrink-0" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {phoneData.status === 'moderate-risk' && (
          <div className="relative overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
            <div className="relative flex gap-4">
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-amber-800 text-lg mb-2">⚠️ MODERATE RISK</h4>
                <p className="text-amber-700 text-sm">
                  This number has some fraud reports. Exercise caution and verify 
                  the caller's identity before sharing any information or making payments.
                </p>
              </div>
            </div>
          </div>
        )}

        {phoneData.status === 'low-risk' && phoneData.total_reports > 0 && (
          <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
            <div className="relative flex gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/30">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-green-800 text-lg mb-2">✅ LOW RISK</h4>
                <p className="text-green-700 text-sm">
                  This number has few fraud reports with low confirmation rate. 
                  However, always remain cautious with unknown callers.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
